import { FormObjectState, ObjectConfig } from "@homebound/form-state";
import { ReactNode } from "react";
import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { BaseChangeEventFormInput } from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import {
  GuidedChangeEventPage_ChangeEventFragment,
  GuidedChangeEventPage_ProjectFragment,
} from "../GuidedChangeEventPage.generated";

export type WorkflowFormState<F> = FormObjectState<F, GuidedChangeEventPage_ChangeEventFragment | undefined>;

// A single step within a workflow. These are pure data: `render` and `isComplete`
// read the forms but must NOT call hooks (all hook logic lives in `useActions`).
export type WorkflowStep<F> = {
  value: GuidedChangeEventStep;
  label: string;
  // Drives the stepper's "complete" checkmark and enables the Next button.
  isComplete: (form: WorkflowFormState<F>) => boolean;
  // Whether the step can be navigated to. Defaults to enabled.
  disabled?: (form: WorkflowFormState<F>) => boolean;
  // Whether the step is left out of the stepper entirely, e.g. an optional step with nothing to show. Defaults to shown.
  hidden?: (form: WorkflowFormState<F>) => boolean;
  render: (form: WorkflowFormState<F>, project: GuidedChangeEventPage_ProjectFragment) => ReactNode;
};

export type WorkflowActionArgs<F extends BaseChangeEventFormInput> = { form: WorkflowFormState<F>; projectId: string };

// What the page hands a workflow's own hooks. It adds the loaded project, so a workflow can branch on
// project facts that aren't (and shouldn't be) form state, e.g. whether the stage has a signed contract.
export type WorkflowPageActionArgs<F extends BaseChangeEventFormInput> = WorkflowActionArgs<F> & {
  project: GuidedChangeEventPage_ProjectFragment;
};

export type WorkflowActions = {
  // Runs when the final "Create" button is clicked.
  onSubmit: () => Promise<void>;
  // Optional side effects run BEFORE advancing past a given step, when Continue is clicked.
  onContinue?: Partial<Record<GuidedChangeEventStep, () => Promise<void>>>;
  // Saves partial progress and leaves. Omit it and the "Save & Exit" button isn't rendered.
  onSaveAndExit?: () => Promise<void>;
};

type WorkflowGateArgs = {
  projectId: string;
  /** The base fields the user just picked in CreateGuidedChangeEventModal, carried into the wizard when the gate passes. */
  input: BaseChangeEventFormInput;
  /** Enters the wizard. The gate calls this once the user has acknowledged / passed it. */
  onContinue: () => void;
  /** Reopens the CreateGuidedChangeEventModal so the user can pick a different workflow, i.e. when the gate blocks them. */
  onChooseAnotherWorkflow: () => void;
};

// The contract every workflow implements. `F` is the workflow's own form shape, which always
// extends the base change-event fields so the wizard can create/save the draft from any workflow.
export type GuidedWorkflow<F extends BaseChangeEventFormInput> = {
  workflow: ChangeEventWorkflow;
  formConfig: ObjectConfig<F>;
  steps: WorkflowStep<F>[];
  // Optional modal shown when the user starts a NEW change event on this workflow, before the wizard mounts,
  // i.e. to warn about a destructive flow or block an ineligible project. Chain further gates by calling
  // `openModal` again from inside the returned content. Skipped in edit mode.
  gate?: (args: WorkflowGateArgs) => ReactNode;
  // Determines the initial step to navigate to when the workflow is first mounted.
  initialStep?: (args: WorkflowPageActionArgs<F>) => GuidedChangeEventStep;
  // Rebuilds the workflow form from an existing change event, so edit mode / a mid-wizard refresh
  // comes back to what was already saved. Workflows that don't save anything yet can skip it.
  mapToForm?: (changeEvent: GuidedChangeEventPage_ChangeEventFragment) => F;
  // A hook, so it may call useMutation / useNavigate / useSnackbar / useModal, etc.
  // Safe to call dynamically because the runner mounts each workflow with a
  // `key={workflow}`, keeping this hook's identity stable per mounted instance.
  useActions: (args: WorkflowPageActionArgs<F>) => WorkflowActions;
};

// Identity helper. Infers `F` from the passed object literal so a workflow's
// `steps` and `useActions` are fully typed against its own form shape at the
// definition site, with no explicit generic needed.
export function defineWorkflow<F extends BaseChangeEventFormInput>(workflow: GuidedWorkflow<F>): GuidedWorkflow<F> {
  return workflow;
}
